import React, {useState, useEffect} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import Footer from "../components/common/footer";
import Works from "../components/homepage/works";
import AllProjects from "../components/projects/allProjects";
import SEOHelmet from "../components/common/SEOHelmet";
import { generateFAQSchema, generateWebPageSchema } from "../utils/schemaGenerators";
import INFO from "../data/user";
import SEO from "../data/seo";
import { reviews } from "../index";
import { faQuoteLeft, faStar, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import {faCalendarDays} from "@fortawesome/free-regular-svg-icons";

import "./styles/homepage.css";

const Homepage = () => {
    const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Auto-rotate reviews
    useEffect(() => {
        const interval = setInterval(() => {
            if (!isAnimating) {
                setIsAnimating(true);
                setTimeout(() => {
                    setCurrentReviewIndex((prevIndex) => 
                        prevIndex === reviews.length - 1 ? 0 : prevIndex + 1
                    );
                    setIsAnimating(false);
                }, 300);
            }
        }, 5000);
        
        return () => clearInterval(interval);
    }, [isAnimating]);

    const worksData = {
        address: "7121 Leesburg Pike, Falls Church, VA 22043"
    };

    const goToNextReview = () => {
        if (!isAnimating) {
            setIsAnimating(true);
            setTimeout(() => {
                setCurrentReviewIndex((prevIndex) => 
                    prevIndex === reviews.length - 1 ? 0 : prevIndex + 1
                );
                setIsAnimating(false);
            }, 300);
        }
    };

    const goToPrevReview = () => {
        if (!isAnimating) {
            setIsAnimating(true);
            setTimeout(() => {
                setCurrentReviewIndex((prevIndex) => 
                    prevIndex === 0 ? reviews.length - 1 : prevIndex - 1
                );
                setIsAnimating(false);
            }, 300);
        }
    };

    const currentSEO = SEO.find((item) => item.page === "home");

    const pageSchema = [
        generateFAQSchema(),
        generateWebPageSchema({
            title: "Expert Hair Salon in Falls Church, VA",
            description: currentSEO.description,
            url: INFO.socials.website
        })
    ];

    return (
        <React.Fragment>
            <SEOHelmet
                title="Expert Hair Salon in Falls Church, VA | Balayage & Color Specialists"
                description={currentSEO.description}
                keywords={currentSEO.keywords.join(", ")}
                schema={pageSchema}
            />

            <div className="page-content">
                <div className="content-wrapper">
                    {/* Hero Section */}
                    <div className="section-title">
                        <h2>Welcome to Pasmo</h2>
                        <p className="section-subtitle">Your neighborhood hair salon in Falls Church, VA</p>
                    </div>
                    <div className="hero-section">
                        <div className="hero-content">
                            <h1>Falls Church Haircuts, Color & Highlights Specialist</h1>
                            <p>Transform your look with expert haircuts, hair coloring and highlights in Falls Church, VA. Master colorist Anna creates stunning, natural-looking results for clients from McLean, Vienna, Reston, Tysons Corner and across Fairfax County & Northern Virginia.</p>
                            <a href={INFO.socials.instagram} target="_blank" rel="noreferrer" className="hero-cta" aria-label="Book balayage appointment at Pasmo Falls Church">
                                Book Now <FontAwesomeIcon icon={faCalendarDays} />
                            </a>
                        </div>
                    </div>

                    <div className="homepage-container">
                        <div className="welcome-section">
                            
                            <p className="welcome-text">{INFO.homepage.description}</p>
                        </div>
                    
                        <div className="homepage-projects">
                            <div className="section-title">
                                <h2>Our Services</h2>
                                <p className="section-subtitle">Discover our range of professional hair services</p>
                            </div>
                            <AllProjects/>
                        </div>

                        <div className="reviews-section">
                            <div className="section-title">
                                <h2>Client Reviews</h2>
                                <p className="section-subtitle">What our clients say about their experience</p>
                            </div>
                            
                            <div className="reviews-slider-container">
                                <button 
                                    className="review-nav-button prev" 
                                    onClick={goToPrevReview}
                                    aria-label="Previous review"
                                >
                                    &lt;
                                </button>
                                
                                <div className="reviews-slider">
                                    <div className={`review-card ${isAnimating ? 'animating' : ''}`}>
                                        <FontAwesomeIcon icon={faQuoteLeft} className="quote-icon" />
                                        <div className="review-rating">
                                            {[...Array(reviews[currentReviewIndex].rating)].map((_, index) => (
                                                <FontAwesomeIcon key={index} icon={faStar} className="star-icon" />
                                            ))}
                                        </div>
                                        <p className="review-text">{reviews[currentReviewIndex].text}</p>
                                        <div className="review-footer">
                                            <p className="review-author">{reviews[currentReviewIndex].author}</p>
                                            <p className="review-service">{reviews[currentReviewIndex].service}</p>
                                        </div>
                                    </div>
                                </div>
                                
                                <button 
                                    className="review-nav-button next" 
                                    onClick={goToNextReview}
                                    aria-label="Next review"
                                >
                                    &gt;
                                </button>
                            </div>
                            
                            <div className="review-indicators">
                                {reviews.map((_, index) => (
                                    <span 
                                        key={index} 
                                        className={`indicator ${index === currentReviewIndex ? 'active' : ''}`}
                                        onClick={() => setCurrentReviewIndex(index)}
                                    ></span>
                                ))}
                            </div>
                        </div>

                        <div className="location-section">
                            <div className="section-title">
                                <h2>Find Us</h2>
                                <p className="section-subtitle">Visit our salon in Falls Church</p>
                            </div>
                            <div className="homepage-works">
                                <Works {...worksData} />
                            </div>
                        </div>
                        
                        <div className="cta-section">
                            <div className="cta-content">
                                <h2>Ready for a transformation?</h2>
                                <p>Book your appointment today and experience the Pasmo difference.</p>
                                <div className="cta-buttons">
                                    <a 
                                        href={INFO.socials.vagaro} 
                                        target="_blank" 
                                        rel="noreferrer" 
                                        className="cta-button primary"
                                    >
                                        Book Appointment <FontAwesomeIcon icon={faCalendarDays} />
                                    </a>
                                    <a 
                                        href={INFO.socials.instagram} 
                                        target="_blank" 
                                        rel="noreferrer" 
                                        className="cta-button secondary"
                                    >
                                        Follow Us on Instagram <FontAwesomeIcon icon={faArrowRight} />
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div className="page-footer">
                            <Footer/>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default Homepage;

